import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {Avatar, DEFAULT_SIZE} from '../avatar/avatar';
import {LeadCountAvatar} from '../lead/lead-count-avatar';
import {Link} from '../link/link';
import type {Participant, Lead} from '../timeline/types';

import styles from './overlapping-avatars.module.css';

// Offset in px to account for border
const BORDER_OFFSET = 4;

type Props = {
    avatars: Participant[];
    relatedLeads?: Lead[];
    maxDisplayed?: number;
    size?: number;
    withTooltip?: boolean;
    getRef?: any;
};

export function OverlappingAvatars(props: Props) {
    const {avatars, maxDisplayed, withTooltip, getRef} = props;

    // Some uses of this component pass duplicate avatars, so we need to filter
    // them out. Ex: Sending am email to multiple emails on the same contact.
    const seenAvatarIds = new Set();
    const uniqueAvatars = avatars.filter((avatar) => {
        if (avatar.id && !seenAvatarIds.has(avatar.id)) {
            seenAvatarIds.add(avatar.id);

            return true;
        }

        return false;
    });

    const size = props.size ? props.size : DEFAULT_SIZE;
    const relatedLeads = props.relatedLeads ? props.relatedLeads : [];
    const displayableParticipants: Participant[] = uniqueAvatars.filter(
        // We do not want to display an avatar for these participant types
        (participant) =>
            !(
                participant.type === 'emailAddress' ||
                participant.type === 'origins' ||
                participant.type === 'leads'
            )
    );

    // Total number of avatars displayed, including related leads and hidden avatar names
    let totalNumDisplayed = relatedLeads.length ? 1 : 0;
    // List of avatars that are displayed
    let displayedAvatars = [...displayableParticipants];
    // List of overflow avatar names that are not displayed
    let hiddenAvatarNames: string[] | null | undefined;

    if (maxDisplayed) {
        const maxAvatars = maxDisplayed - totalNumDisplayed;
        const numHidden = displayableParticipants.length - maxAvatars;

        // If the number of hidden avatars is more than one, we will display them in a +X list
        if (numHidden > 1) {
            displayedAvatars = displayableParticipants.slice(0, maxAvatars);
            hiddenAvatarNames = displayableParticipants
                .slice(maxAvatars)
                .map((avatar) => avatar.name);

            // Update total displayed to include +X hidden
            totalNumDisplayed++;
        }
    }

    // Update total number displayed
    totalNumDisplayed += displayedAvatars.length;

    React.useEffect(() => {
        ReactTooltip.rebuild();

        return () => ReactTooltip.hide();
    }, [hiddenAvatarNames]);

    const iconSeparation: number = Math.ceil(0.85 * size);
    const indexOffset = relatedLeads.length ? 1 : 0;

    return (
        <div
            ref={getRef}
            style={{
                height: size + BORDER_OFFSET,
                // Width of avatar list
                width: size + iconSeparation * (totalNumDisplayed - 1) + BORDER_OFFSET,
            }}
            className={styles['avatar-list']}
        >
            {relatedLeads.length ? (
                <div
                    style={{
                        width: size + BORDER_OFFSET,
                        height: size + BORDER_OFFSET,
                    }}
                    className={styles['avatar-wrapper']}
                >
                    <LeadCountAvatar leads={relatedLeads} size={size} />
                </div>
            ) : undefined}
            {displayedAvatars.map((entity, index) => {
                // Display avatars with htmlUrls as clickable links
                const Tag = entity.htmlUrl ? Link : 'div';
                let linkProps: Record<string, any> = {};
                if (entity.htmlUrl) {
                    linkProps = {
                        href: entity.htmlUrl,
                    };
                }

                return (
                    <Tag
                        key={entity.id}
                        style={{
                            left: (index + indexOffset) * iconSeparation,
                            zIndex: index + indexOffset,
                            width: size + BORDER_OFFSET,
                            height: size + BORDER_OFFSET,
                        }}
                        data-tip={withTooltip ? entity.name : undefined}
                        data-place='bottom'
                        // Needed to prevent modal from popping up when clickable avatar is clicked
                        onClick={(e) => {
                            if (entity.htmlUrl) {
                                e.stopPropagation();
                            }
                        }}
                        {...linkProps}
                        className={
                            entity.htmlUrl
                                ? styles['avatar-wrapper--link']
                                : styles['avatar-wrapper']
                        }
                    >
                        <Avatar
                            size={size}
                            avatarUrl={entity.avatarUrl}
                            type={entity.type}
                            initials={entity.initials || ''}
                            circular={true}
                            border={true}
                        />
                    </Tag>
                );
            })}
            {hiddenAvatarNames && maxDisplayed ? (
                <div
                    style={{
                        left: maxDisplayed * iconSeparation,
                        zIndex: maxDisplayed,
                        width: size,
                        height: size,
                        borderRadius: size,
                    }}
                    data-tip={withTooltip ? hiddenAvatarNames.join('<br />') : undefined}
                    data-place='bottom'
                    className={styles['hidden-avatars']}
                >
                    +{hiddenAvatarNames.length}
                </div>
            ) : undefined}
        </div>
    );
}
